<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter, type RouteLocationRaw } from 'vue-router'
import {
    Connection,
    Document,
    Files,
    Help,
    Menu as MenuIcon,
    Monitor,
    Opportunity,
    Reading
} from '@element-plus/icons-vue'

import OrganizationBar from '@/components/utilities/OrganizationBar.vue'

const HELP_URL = 'https://prizific.atlassian.net/servicedesk/customer/portal/269'
const KNOWLEDGE_URL = 'https://prizific.atlassian.net/wiki'
const SUGGEST_FEATURE_URL =
    'https://prizific.atlassian.net/servicedesk/customer/portal/269/group/319/create/364'

const route = useRoute()
const router = useRouter()
const isPanelOpen = ref(false)

const isCampaignActive = computed(() => route.path.startsWith('/campaign-manager'))
const isHiveActive = computed(() => route.path.startsWith('/hive-manager'))

function navigateTo(target: RouteLocationRaw) {
    isPanelOpen.value = false
    void router.push(target)
}

function openExternal(url: string) {
    isPanelOpen.value = false
    window.open(url, '_blank', 'noopener,noreferrer')
}

watch(
    () => route.fullPath,
    () => {
        isPanelOpen.value = false
    }
)
</script>

<template>
    <div class="mobile-menu">
        <Transition name="mobile-panel">
            <div
                v-if="isPanelOpen"
                class="mobile-menu__backdrop"
                role="presentation"
                @click.self="isPanelOpen = false"
            >
                <section
                    id="mobile-navigation-panel"
                    class="mobile-menu__panel"
                    role="dialog"
                    aria-modal="true"
                    aria-label="Menu"
                >
                    <OrganizationBar />

                    <div class="mobile-menu__panel-content">
                        <h2>Menu</h2>

                        <div class="mobile-menu__cards">
                            <button type="button" @click="navigateTo({ name: 'sites' })">
                                <el-icon><Monitor /></el-icon>
                                <span>Sites (beta)</span>
                            </button>
                            <button type="button" @click="openExternal(KNOWLEDGE_URL)">
                                <el-icon><Reading /></el-icon>
                                <span>Knowledge</span>
                            </button>
                            <button type="button" @click="navigateTo({ name: 'news' })">
                                <el-icon><Document /></el-icon>
                                <span>{{ $t('adminSubMenu.news') }}</span>
                            </button>
                            <button type="button" @click="openExternal(SUGGEST_FEATURE_URL)">
                                <el-icon><Opportunity /></el-icon>
                                <span>{{ $t('adminSubMenu.suggestAFeature') }}</span>
                            </button>
                        </div>
                    </div>
                </section>
            </div>
        </Transition>

        <nav class="mobile-menu__bar" aria-label="Primary navigation">
            <button
                type="button"
                :class="{ active: isCampaignActive }"
                :aria-current="isCampaignActive ? 'page' : undefined"
                @click="navigateTo({ name: 'campaign-list', params: { state: 'active' } })"
            >
                <el-icon><Files /></el-icon>
                <span>Campaigns</span>
            </button>
            <button
                type="button"
                :class="{ active: isHiveActive }"
                :aria-current="isHiveActive ? 'page' : undefined"
                @click="navigateTo({ name: 'hives' })"
            >
                <el-icon><Connection /></el-icon>
                <span>Hives</span>
            </button>
            <button type="button" @click="openExternal(HELP_URL)">
                <el-icon><Help /></el-icon>
                <span>{{ $t('adminSubMenu.help') }}</span>
            </button>
            <button
                type="button"
                :class="{ active: isPanelOpen }"
                :aria-expanded="isPanelOpen"
                aria-controls="mobile-navigation-panel"
                @click="isPanelOpen = !isPanelOpen"
            >
                <el-icon><MenuIcon /></el-icon>
                <span>Menu</span>
            </button>
        </nav>
    </div>
</template>

<style lang="scss" scoped>
.mobile-menu {
    --mobile-menu-height: calc(64px + env(safe-area-inset-bottom));

    &__backdrop {
        position: fixed;
        inset: 0 0 var(--mobile-menu-height);
        z-index: 998;
        display: flex;
        align-items: flex-end;
        background: rgba(0, 0, 0, 0.35);
    }

    &__panel {
        display: flex;
        flex-direction: column;
        width: 100%;
        height: calc(100dvh - var(--mobile-menu-height));
        overflow-y: auto;
        background: var(--el-bg-color);
        border-radius: 16px 16px 0 0;
        box-shadow: var(--el-box-shadow-dark);
    }

    &__panel-content {
        display: flex;
        flex: 1;
        flex-direction: column;
        justify-content: flex-end;
        padding: 24px 16px 38px;

        h2 {
            margin: 0 0 28px;
            color: var(--el-text-color-primary);
            font-size: 24px;
            text-align: center;
        }
    }

    &__cards {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 20px;

        button {
            display: flex;
            min-height: 88px;
            flex-direction: column;
            align-items: flex-start;
            justify-content: space-between;
            padding: 16px;
            color: var(--el-text-color-primary);
            background: var(--el-fill-color-light);
            border: 1px solid var(--el-border-color-light);
            border-radius: var(--el-border-radius-base);
            cursor: pointer;
            text-align: left;

            &:active {
                border-color: var(--el-color-primary);
            }
        }
    }

    &__bar {
        position: fixed;
        right: 0;
        bottom: 0;
        left: 0;
        z-index: 1000;
        display: grid;
        min-height: var(--mobile-menu-height);
        grid-template-columns: repeat(4, 1fr);
        padding-bottom: env(safe-area-inset-bottom);
        background: var(--el-menu-bg-color);
        border-top: 1px solid var(--el-border-color-lighter);
        box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.08);

        button {
            display: flex;
            min-width: 0;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            gap: 5px;
            padding: 8px 4px;
            color: var(--el-text-color-regular);
            background: transparent;
            border: 0;
            cursor: pointer;
            font-size: 12px;

            .el-icon {
                font-size: 20px;
            }

            &.active {
                color: var(--el-color-primary);
            }
        }
    }
}

.mobile-panel-enter-active,
.mobile-panel-leave-active {
    transition: background-color 0.25s ease;

    .mobile-menu__panel {
        transition: transform 0.25s ease;
    }
}

.mobile-panel-enter-from,
.mobile-panel-leave-to {
    background-color: transparent;

    .mobile-menu__panel {
        transform: translateY(100%);
    }
}
</style>
